import { createContext, useContext, useState, useEffect, useCallback, ReactNode } from 'react';
import { supabase } from '@/lib/supabase';

export type FeatureKey =
  | 'dashboard'
  | 'accounts'
  | 'transactions'
  | 'budgets'
  | 'goals'
  | 'calendar'
  | 'ai_chat'
  | 'settings';

interface FeatureFlag {
  id: string;
  key: FeatureKey;
  label: string;
  description: string | null;
  enabled: boolean;
  updated_at: string;
}

export interface MaintenanceMode {
  enabled: boolean;
  start_time: string | null;
  end_time: string | null;
  message: string | null;
}

interface FeatureFlagContextValue {
  flags: FeatureFlag[];
  isLoading: boolean;
  isEnabled: (key: FeatureKey) => boolean;
  toggleFlag: (key: FeatureKey, enabled: boolean) => Promise<void>;
  refreshFlags: () => Promise<void>;
  maintenance: MaintenanceMode;
  updateMaintenance: (data: Partial<MaintenanceMode>) => Promise<void>;
}

const FeatureFlagContext = createContext<FeatureFlagContextValue | null>(null);

const POLL_INTERVAL = 30_000; // 30 seconds

const DEFAULT_MAINTENANCE: MaintenanceMode = {
  enabled: false,
  start_time: null,
  end_time: null,
  message: null,
};

export function FeatureFlagProvider({ children }: { children: ReactNode }) {
  const [flags, setFlags] = useState<FeatureFlag[]>([]);
  const [maintenance, setMaintenance] = useState<MaintenanceMode>(DEFAULT_MAINTENANCE);
  const [isLoading, setIsLoading] = useState(true);

  const fetchFlags = useCallback(async () => {
    const [flagsRes, maintenanceRes] = await Promise.all([
      supabase.from('feature_flags').select('*').order('label'),
      supabase.from('maintenance_mode').select('*').eq('id', 1).single(),
    ]);

    if (!flagsRes.error && flagsRes.data) {
      setFlags(flagsRes.data as FeatureFlag[]);
    }
    if (!maintenanceRes.error && maintenanceRes.data) {
      setMaintenance(maintenanceRes.data as MaintenanceMode);
    }
    setIsLoading(false);
  }, []);

  // Initial load
  useEffect(() => {
    fetchFlags();
  }, [fetchFlags]);

  // Poll for changes (since Realtime isn't enabled)
  useEffect(() => {
    const interval = setInterval(fetchFlags, POLL_INTERVAL);
    return () => clearInterval(interval);
  }, [fetchFlags]);

  const isEnabled = useCallback(
    (key: FeatureKey): boolean => {
      const flag = flags.find((f) => f.key === key);
      // Default to true if flag not found (fail-open for missing flags)
      return flag?.enabled ?? true;
    },
    [flags]
  );

  const toggleFlag = useCallback(async (key: FeatureKey, enabled: boolean) => {
    const { error } = await supabase
      .from('feature_flags')
      .update({ enabled, updated_at: new Date().toISOString() })
      .eq('key', key);

    if (!error) {
      // Optimistic update
      setFlags((prev) =>
        prev.map((f) =>
          f.key === key ? { ...f, enabled, updated_at: new Date().toISOString() } : f
        )
      );
    } else {
      throw error;
    }
  }, []);

  const updateMaintenance = useCallback(async (data: Partial<MaintenanceMode>) => {
    const { error } = await supabase
      .from('maintenance_mode')
      .update({ ...data, updated_at: new Date().toISOString() })
      .eq('id', 1);

    if (!error) {
      setMaintenance((prev) => ({ ...prev, ...data }));
    } else {
      throw error;
    }
  }, []);

  return (
    <FeatureFlagContext.Provider
      value={{
        flags,
        isLoading,
        isEnabled,
        toggleFlag,
        refreshFlags: fetchFlags,
        maintenance,
        updateMaintenance,
      }}
    >
      {children}
    </FeatureFlagContext.Provider>
  );
}

export function useFeatureFlags() {
  const ctx = useContext(FeatureFlagContext);
  if (!ctx) throw new Error('useFeatureFlags must be used within FeatureFlagProvider');
  return ctx;
}
